import { useState } from "react";
import { Link } from "react-router-dom";
import { Diamond, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <Diamond className="w-6 h-6 text-zinc-900" strokeWidth={1.5} />
          <span className="font-display text-lg font-semibold text-zinc-900">Shinning Eyes</span>
        </div>

        {sent ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-6 text-center" data-testid="forgot-password-sent">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="font-display text-xl font-semibold text-zinc-900">Check your email</h2>
            <p className="text-sm text-zinc-500 mt-2">
              If <span className="font-medium text-zinc-700">{email}</span> is a registered account, we've sent a password
              reset link. It expires in 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-900 font-medium mt-5 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">Recover</p>
              <h2 className="font-display text-3xl font-semibold text-zinc-900">Forgot password</h2>
              <p className="text-sm text-zinc-500 mt-2">Enter your account email and we'll send you a reset link.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-11" disabled={loading} data-testid="forgot-submit-button">
                <Mail className="w-4 h-4 mr-2" />
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-600 mt-6 hover:text-zinc-900" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
